import {analytics} from '../../services/analytics';
import type {UniversityVideo} from '../../services/universityService';

// Shared PostHog helpers for Opalite University. Keeps event names + property
// shapes consistent across the player modal, /start, tips, and Learn.

export const UNIVERSITY_PROGRESS_MILESTONES = [25, 50, 75] as const;

export type UniversityVideoCloseReason =
  | 'done'
  | 'next'
  | 'dismiss'
  | 'navigate_away';

export interface UniversityWatchSnapshot {
  video_id: string;
  video_title: string;
  duration_sec: number;
  watch_time_sec: number;
  max_position_sec: number;
  percent_watched: number;
  completed: boolean;
  placeholder: boolean;
  source: string;
  playlist_index: number;
  playlist_length: number;
  is_playlist: boolean;
}

/** Mutable watch-session accumulator for one open of one video. */
export interface UniversityWatchSession {
  videoId: string;
  openedAt: number;
  maxPositionSec: number;
  accumulatedPlayMs: number;
  lastPlayAt: number | null;
  started: boolean;
  completed: boolean;
  closed: boolean;
  milestonesFired: Set<number>;
}

export function createWatchSession(videoId: string): UniversityWatchSession {
  return {
    videoId,
    openedAt: Date.now(),
    maxPositionSec: 0,
    accumulatedPlayMs: 0,
    lastPlayAt: null,
    started: false,
    completed: false,
    closed: false,
    milestonesFired: new Set(),
  };
}

export function flushPlayTime(session: UniversityWatchSession): void {
  if (session.lastPlayAt == null) return;
  session.accumulatedPlayMs += Date.now() - session.lastPlayAt;
  session.lastPlayAt = null;
}

export function markPlaying(session: UniversityWatchSession): void {
  if (session.lastPlayAt == null) {
    session.lastPlayAt = Date.now();
  }
}

export function notePosition(
  session: UniversityWatchSession,
  currentTimeSec: number,
): void {
  if (currentTimeSec > session.maxPositionSec) {
    session.maxPositionSec = currentTimeSec;
  }
}

export function watchSnapshot(
  session: UniversityWatchSession,
  video: UniversityVideo,
  opts: {
    source: string;
    playlistIndex: number;
    playlistLength: number;
  },
): UniversityWatchSnapshot {
  // Include the in-progress play segment without flushing — callers that are
  // leaving the video (pause/close/complete) flush explicitly first.
  let playMs = session.accumulatedPlayMs;
  if (session.lastPlayAt != null) {
    playMs += Date.now() - session.lastPlayAt;
  }
  const durationSec =
    video.durationSec > 0 ? video.durationSec : session.maxPositionSec;
  const watchTimeSec = Math.round(playMs / 1000);
  const maxPositionSec = Math.round(session.maxPositionSec);
  const percentWatched =
    durationSec > 0
      ? Math.min(100, Math.round((session.maxPositionSec / durationSec) * 100))
      : session.completed
        ? 100
        : 0;

  return {
    video_id: video.id,
    video_title: video.title,
    duration_sec: video.durationSec,
    watch_time_sec: watchTimeSec,
    max_position_sec: maxPositionSec,
    percent_watched: percentWatched,
    completed: session.completed,
    placeholder: !video.hasVideo,
    source: opts.source,
    playlist_index: opts.playlistIndex,
    playlist_length: opts.playlistLength,
    is_playlist: opts.playlistLength > 1,
  };
}

export function captureUniversity(
  eventName: string,
  properties?: Record<string, any>,
): void {
  analytics.capture(eventName, properties);
}

export function captureVideoOpened(
  video: UniversityVideo,
  opts: {
    source: string;
    playlistIndex: number;
    playlistLength: number;
  },
): void {
  captureUniversity('university_video_opened', {
    video_id: video.id,
    video_title: video.title,
    duration_sec: video.durationSec,
    placeholder: !video.hasVideo,
    source: opts.source,
    playlist_index: opts.playlistIndex,
    playlist_length: opts.playlistLength,
    is_playlist: opts.playlistLength > 1,
  });
}

export function captureVideoStarted(snapshot: UniversityWatchSnapshot): void {
  captureUniversity('university_video_started', snapshot);
}

export function captureVideoProgress(
  snapshot: UniversityWatchSnapshot,
  milestone: number,
): void {
  captureUniversity('university_video_progress', {
    ...snapshot,
    milestone_percent: milestone,
  });
}

export function captureVideoCompleted(snapshot: UniversityWatchSnapshot): void {
  captureUniversity('university_video_completed', snapshot);
}

export function captureVideoClosedFromSession(
  session: UniversityWatchSession,
  video: UniversityVideo,
  opts: {
    source: string;
    playlistIndex: number;
    playlistLength: number;
    closeReason: UniversityVideoCloseReason;
  },
): void {
  if (session.closed) return;
  session.closed = true;
  const snapshot = watchSnapshot(session, video, opts);
  captureUniversity('university_video_closed', {
    ...snapshot,
    close_reason: opts.closeReason,
    open_duration_sec: Math.max(
      0,
      Math.round((Date.now() - session.openedAt) / 1000),
    ),
  });
}

export function maybeCaptureProgressMilestones(
  session: UniversityWatchSession,
  video: UniversityVideo,
  opts: {
    source: string;
    playlistIndex: number;
    playlistLength: number;
  },
): void {
  if (!video.hasVideo || video.durationSec <= 0) return;
  const percent = Math.min(
    100,
    Math.round((session.maxPositionSec / video.durationSec) * 100),
  );
  for (const milestone of UNIVERSITY_PROGRESS_MILESTONES) {
    if (percent >= milestone && !session.milestonesFired.has(milestone)) {
      session.milestonesFired.add(milestone);
      captureVideoProgress(watchSnapshot(session, video, opts), milestone);
    }
  }
}
